/**
 * Console filters to suppress known WebContainer and tracking prevention warnings
 */

// Store original console methods
const originalConsoleError = console.error;
const originalConsoleWarn = console.warn;

// List of error/warning patterns to suppress
const suppressPatterns = [
  /Tracking Prevention blocked access to storage/i,
  /Failed to execute 'postMessage' on 'DOMWindow'/i,
  /The target origin provided .* does not match the recipient window's origin/i,
  /\[Contextify\] \[WARNING\]/i,
  /was preloaded using link preload but not used/i,
  /task queue exceeded allotted deadline/i,
  /Generic cosmetic filtering stopped/i,
  /\[Storage\].*is not available\. Using in-memory fallback/i,
  /SecurityError.*localStorage/i,
  /Access is denied for this document/i,
  /reportAllChanges/i,
  /Cannot read properties of undefined \(reading 'startTime'\)/i,
  /unsupported nested scroll container detected/i,
];

/**
 * Check if a message should be suppressed
 */
function shouldSuppress(args: unknown[]): boolean {
  const message = args
    .map((arg) => (typeof arg === 'string' ? arg : String(arg)))
    .join(' ');

  return suppressPatterns.some((pattern) => pattern.test(message));
}

/**
 * Override console.error to filter out known issues
 */
console.error = (...args: unknown[]) => {
  if (!shouldSuppress(args)) {
    originalConsoleError.apply(console, args);
  }
};

/**
 * Override console.warn to filter out known issues
 */
console.warn = (...args: unknown[]) => {
  if (!shouldSuppress(args)) {
    originalConsoleWarn.apply(console, args);
  }
};

/**
 * Safe storage wrapper that catches errors silently
 */
function createSafeStorage(storage: Storage): Storage {
  return new Proxy(storage, {
    get(target, prop) {
      try {
        const value = target[prop as keyof Storage];
        if (typeof value === 'function') {
          return function (...args: unknown[]) {
            try {
              return value.apply(target, args);
            } catch (e) {
              // Silently fail - tracking prevention is blocking access
              return null;
            }
          };
        }
        return value;
      } catch (e) {
        // Silently fail
        return null;
      }
    },
    set(target, prop, value) {
      try {
        target[prop as keyof Storage] = value;
        return true;
      } catch (e) {
        // Silently fail
        return true;
      }
    },
  });
}

/**
 * Initialize safe storage wrappers
 */
export function initializeConsolFilters() {
  try {
    // Wrap localStorage and sessionStorage with safe proxies
    if (typeof window !== 'undefined') {
      try {
        Object.defineProperty(window, 'localStorage', {
          value: createSafeStorage(window.localStorage),
          writable: false,
          configurable: true,
        });
      } catch (e) {
        // Already defined or blocked
      }

      try {
        Object.defineProperty(window, 'sessionStorage', {
          value: createSafeStorage(window.sessionStorage),
          writable: false,
          configurable: true,
        });
      } catch (e) {
        // Already defined or blocked
      }

      // Suppress window.postMessage errors
      const originalPostMessage = window.postMessage.bind(window);
      window.postMessage = function (message: any, targetOriginOrOptions?: any, transfer?: any) {
        try {
          return originalPostMessage(message, targetOriginOrOptions, transfer);
        } catch (e) {
          // Silently fail - WebContainer postMessage errors
        }
      };

      // Suppress unhandled extension errors (e.g. VM script reportAllChanges)
      window.addEventListener('error', (event) => {
        if (
          event.message &&
          (event.message.includes('reportAllChanges') ||
           event.message.includes("reading 'startTime'"))
        ) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      });
    }
  } catch (e) {
    // If any initialization fails, continue silently
  }
}
